// C-2: the browse screen's own header — wordmark, search, state tabs,
// filter pills. This is NOT the site-wide Header.js; it lives inside the
// browse module and only governs the browse grid.
//
// Desktop (sm: and up):
// Row 1: wordmark + search. (SOR-626: the "My schedule" pill and avatar
// were removed — unused affordances with no screens behind them.)
// Row 2: state tabs (All / Live N / Upcoming N / Saved) on the left,
// Park/Sort dropdown pills pushed to the RIGHT edge — visually separated
// from the tabs so they never read as a fourth state (the current build's
// inline "Filter ⌄" regression).
//
// SOR-625 mobile (<540px, base classes):
// Row 1 is 52px/16px-padding — wordmark and a 44x44 search icon that
// expands to a full-width field on tap.
// Row 2 is one horizontally scrolling non-wrapping chip row (34px chips,
// 9px radius, 7px gap, active chip pinned left via flex order); Park + Sort
// move into the FilterSheet, opened by the mobile-only "Filter" chip.
//
// Tabs follow the WAI-ARIA tabs pattern: role=tablist/tab, aria-selected,
// roving tabindex, arrow-key navigation with automatic activation.
//
// All colors come from the browse-* tokens (raw hex fails the designTokens
// jest check).
import React, { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { SearchIcon, ChevronDownIcon, CloseIcon } from './icons';

export const FILTERS = ['all', 'live', 'upcoming', 'saved'];
const TAB_LABELS = { all: 'All', live: 'Live', upcoming: 'Upcoming', saved: 'Saved' };

// SOR-625 mobile chip geometry, shared by the state chips and the Filter
// chip: 34px tall, 9px radius (arbitrary spelling — Bootstrap's
// .rounded-pill is !important), and a before: pseudo that pads the 34px
// chip out to a 46px touch target. Display (flex/hidden) is per-chip.
const MOBILE_CHIP_CLS =
  'relative before:absolute before:inset-x-0 before:top-[-6px] before:bottom-[-6px] h-[34px] shrink-0 items-center gap-1.75 whitespace-nowrap rounded-[9px] px-3.5 font-inter-tight text-13 font-medium leading-none';

// Sort: a native <select> dressed as the design's pill, same treatment as
// ParkSelect. "Rising" appears only when the live payload carries the surge
// fields (server kill switch on) — a sort option that does nothing is a lie.
const SortSelect = ({ sort, onSortChange, showRising }) => (
  <label className="relative flex h-8 items-center rounded-tab border border-browse-border-strong font-inter-tight text-13 font-medium leading-none text-browse-text-control hover:border-browse-border-hover">
    <span className="sr-only">Sort live streams</span>
    <select
      value={sort}
      onChange={(e) => {
        onSortChange(e.target.value);
        posthog.capture('browse_sort_changed', { sort: e.target.value });
      }}
      className="h-full cursor-pointer appearance-none rounded-tab bg-transparent pl-3 pr-8 font-inter-tight text-13 font-medium text-browse-text-control focus:outline-none focus-visible:ring-2 focus-visible:ring-browse-accent"
    >
      <option value="watched">Sort: Most watched</option>
      {showRising && <option value="rising">Sort: Rising</option>}
    </select>
    <ChevronDownIcon
      aria-hidden="true"
      className="pointer-events-none absolute right-3 text-browse-icon-muted"
    />
  </label>
);

// Park filter: a native <select> dressed as the design's pill — the
// platform's dropdown, no menu JS. Lists every park/resort tag present
// in the fetched streams.
const ParkSelect = ({ parks, park, onParkChange }) => (
  <label className="relative flex h-8 items-center rounded-tab border border-browse-border-strong font-inter-tight text-13 font-medium leading-none text-browse-text-control hover:border-browse-border-hover">
    <span className="sr-only">Filter by park or resort</span>
    <select
      value={park}
      onChange={(e) => {
        onParkChange(e.target.value);
        posthog.capture('browse_park_filtered', { park: e.target.value });
      }}
      className="h-full cursor-pointer appearance-none rounded-tab bg-transparent pl-3 pr-8 font-inter-tight text-13 font-medium text-browse-text-control focus:outline-none focus-visible:ring-2 focus-visible:ring-browse-accent"
    >
      <option value="all">Park: All</option>
      {parks.map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
    <ChevronDownIcon
      aria-hidden="true"
      className="pointer-events-none absolute right-3 text-browse-icon-muted"
    />
  </label>
);

/**
 * @param {Object} props
 * @param {string} props.query
 * @param {(q: string) => void} props.onQueryChange
 * @param {'all'|'live'|'upcoming'|'saved'} props.filter
 * @param {(f: string) => void} props.onFilterChange
 * @param {{live?: number|null, upcoming?: number|null}} [props.counts]
 *   live tab counts; a null/undefined count is OMITTED from the label
 *   (never rendered as 0)
 * @param {() => void} [props.onOpenFilterSheet]  SOR-625: opens the mobile
 *   Park/Sort bottom sheet (the "Filter" chip)
 */
const BrowseHeader = ({
  query,
  onQueryChange,
  filter,
  onFilterChange,
  counts = {},
  parks = [],
  park = 'all',
  onParkChange = () => {},
  sort = 'watched',
  onSortChange = () => {},
  showRising = false,
  onOpenFilterSheet = () => {},
}) => {
  const tabRefs = useRef({});
  // SOR-625: mobile search starts collapsed to its 44x44 icon; expanding
  // reveals the ONE shared input (desktop always shows it via sm:flex).
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const onTabKeyDown = (e) => {
    const i = FILTERS.indexOf(filter);
    let next = null;
    if (e.key === 'ArrowRight') next = FILTERS[(i + 1) % FILTERS.length];
    else if (e.key === 'ArrowLeft')
      next = FILTERS[(i - 1 + FILTERS.length) % FILTERS.length];
    else if (e.key === 'Home') next = FILTERS[0];
    else if (e.key === 'End') next = FILTERS[FILTERS.length - 1];
    if (next) {
      e.preventDefault();
      onFilterChange(next);
      tabRefs.current[next]?.focus();
    }
  };

  return (
    <header className="border-b border-browse-divider bg-browse-surface">
      {/* Row 1 — mobile: 52px, wordmark · search icon (expands to a
          full-width field); desktop: wordmark + always-open search */}
      {/* Bootstrap-collision rule: base classes whose names Bootstrap also
          defines (gap-3, px-4, rounded-pill, …) are written as arbitrary
          values (gap-[12px]) — Bootstrap's !important utilities otherwise
          override both the mobile value and the sm: desktop restore. */}
      <div className="flex h-[52px] items-center gap-[12px] px-[16px] sm:h-auto sm:gap-7 sm:px-7 sm:py-4.5">
        <img
          src="https://storage.googleapis.com/streamspheres-django/stream-park-logo.png"
          alt="Stream Park"
          className={`${searchOpen ? 'hidden sm:block ' : ''}h-7 w-auto shrink-0 sm:h-10`}
        />
        <label
          className={`${searchOpen ? 'flex' : 'hidden'} h-11 min-w-0 flex-1 items-center gap-2.5 rounded-search border border-browse-border-input bg-browse-fill-subtle px-3.5 text-browse-text-placeholder focus-within:border-browse-accent sm:flex sm:h-[38px] sm:max-w-[520px]`}
        >
          <SearchIcon aria-hidden="true" className="shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search channels, parks, streams"
            aria-label="Search channels, parks, streams"
            className="w-full border-0 bg-transparent p-0 font-inter-tight text-sm font-normal text-browse-text-primary placeholder:text-browse-text-placeholder focus:outline-none focus:ring-0"
          />
        </label>
        {searchOpen ? (
          <button
            type="button"
            aria-label="Close search"
            onClick={() => {
              // collapsing also clears the query — an invisible active
              // filter behind a bare icon silently empties the grid
              setSearchOpen(false);
              onQueryChange('');
            }}
            className="flex h-11 w-11 shrink-0 items-center justify-center text-browse-icon-muted sm:hidden"
          >
            <CloseIcon aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Search"
            onClick={() => {
              setSearchOpen(true);
              posthog.capture('browse_search_opened');
            }}
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center text-browse-text-control sm:hidden"
          >
            <SearchIcon aria-hidden="true" size={18} />
          </button>
        )}
      </div>

      {/* Row 2 — mobile: one scrolling chip row + "Filter" chip;
          desktop: state tabs left, Park/Sort pills at the right edge */}
      <div className="flex h-[46px] items-center gap-1.75 overflow-x-auto border-t border-browse-divider-subtle px-[16px] sm:h-12 sm:gap-1.5 sm:overflow-visible sm:px-7">
        <div
          role="tablist"
          aria-label="Stream state"
          className="flex items-center gap-1.75 sm:gap-1.5"
          onKeyDown={onTabKeyDown}
        >
          {FILTERS.map((f) => {
            const active = f === filter;
            const count =
              f === 'live'
                ? counts.live
                : f === 'upcoming'
                ? counts.upcoming
                : null;
            return (
              <button
                key={f}
                ref={(el) => {
                  tabRefs.current[f] = el;
                }}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls="browse-streams"
                tabIndex={active ? 0 : -1}
                onClick={() => {
                  onFilterChange(f);
                  posthog.capture('browse_filter_changed', { filter: f });
                }}
                // active pinned left via order-[-1] (bare order-first
                // collides with Bootstrap's !important utility); Saved
                // lives in the tab bar below sm, so its chip is desktop-only
                className={`${f === 'saved' ? 'hidden sm:flex' : 'flex'} ${MOBILE_CHIP_CLS} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-browse-accent sm:h-8 sm:rounded-tab ${
                  active
                    ? 'order-[-1] sm:order-[0] bg-browse-text-primary text-browse-surface'
                    : 'text-browse-text-secondary hover:bg-browse-fill-subtle'
                }`}
              >
                {f === 'live' && (
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 rounded-full bg-browse-live"
                  />
                )}
                {/* count appended into the label, omitted when unavailable */}
                {TAB_LABELS[f]}
                {count != null && ` ${count}`}
              </button>
            );
          })}
        </div>
        {/* SOR-625: Park + Sort live in the bottom sheet below sm */}
        <button
          type="button"
          onClick={() => {
            posthog.capture('browse_filter_sheet_opened');
            onOpenFilterSheet();
          }}
          className={`flex ${MOBILE_CHIP_CLS} border border-browse-border-strong text-browse-text-control sm:hidden`}
        >
          Filter
          <ChevronDownIcon aria-hidden="true" className="text-browse-icon-muted" />
        </button>
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          <ParkSelect parks={parks} park={park} onParkChange={onParkChange} />
          <SortSelect sort={sort} onSortChange={onSortChange} showRising={showRising} />
        </div>
      </div>
    </header>
  );
};

export default BrowseHeader;
